<template>
  <div class="cid-setting"
       :class="{ confirming } ">
    <div class="step loading"
         v-if="loading">
      <ui-loading-circle :showLoadingText="true" />
    </div>
    <div v-else-if="confirming"
         class="confirm">
      <div class="confirm-icon">
        <i class="icon icon--dialog-warning"></i>
      </div>
      <div class="confirm-description"
           v-html="confirmOption.description"></div>
      <div class="confirm-buttons">
        <ui-button @click="confirming = false">
          {{ $t('common.no') }}
        </ui-button>
        <ui-button @click="confirmOption.confirm"
                   :primary2="true">
          {{ $t('common.yes') }}
        </ui-button>
      </div>
    </div>
    <div class="step step-1"
         v-else-if="step === 1">
      <div class="cid-header">
        <i class="icon icon--cid"></i>
        <h3>
          <span v-html="$t('campaignTargeting.cid.name')"></span>
          <ui-icon-tooltip :iconSize="'normal'"
                           arrowBottom
                           arrowCenter>
            <span v-html="$t('campaignTargeting.cid.tooltip')"></span>
          </ui-icon-tooltip>
        </h3>
        <p v-html="$t('campaignTargeting.cid.description')"></p>
      </div>
      <div class="cid-body">
        <div v-if="hasData"
             class="cid-selection">
          <div class="null-inflow">
            {{ $t('campaignTargeting.cid.nullInflow') }}
            <ui-switch :on="this.selectedCid[0] || false"
                       @click="toggleWithoutValue()" />
          </div>
          <button v-for="tag of tagList"
                  :key="`cidButton${tag}`"
                  @click="showList(tag)"
                  class="tag-button"
                  :class="{ selected: selectedCid[tag] && selectedCid[tag].length > 0,
                            seg: tag === '10' }">
            <span class="name"
                  v-html="$t('campaignTargeting.cid.' + tag)"></span>
            <span v-if="selectedCid[tag] && selectedCid[tag].length > 0"
                  class="count">
              <span class="check">
                <i class="icon icon--check-white"></i>
              </span>
              <span class="number">
                {{ selectedCid[tag].length | thousand }}
              </span>
              {{ $t('common.selected') }}
            </span>
            <i v-else
               class="icon icon--plus"></i>
          </button>
        </div>
        <div v-else
             class="cid-empty">
          <i class="icon icon--empty"></i><br>
          {{ $t('common.noCollectedData') }}
        </div>
      </div>
      <div class="cid-footer">
        <ui-button class="back"
                   :iconName="'icon--arrow-left-black'"
                   @click="onCloseLayer(false)">{{ $t('common.back') }}</ui-button>
        <ui-button @click="onCloseLayer(false, true)">{{ $t('common.close') }}</ui-button>
      </div>
    </div>
    <div class="step step-2"
         v-else-if="step === 2">
      <div class="cid-header">
        <button type="button"
                class="btn-close"
                @click="onCloseLayer(true, true)">
          <i class="icon icon--close-size16"></i>
        </button>
        <h2 v-html="$t('campaignTargeting.cid.selecting.title', { tag: $t('campaignTargeting.cid.' + selectedTag) })"></h2>
        <p v-html="$t('campaignTargeting.cid.selecting.description', { tag: $t('campaignTargeting.cid.' + selectedTag) })"></p>
      </div>
      <div class="cid-body"
           :key="resetKey">
        <ui-check-selectbox v-if="listItems"
                            :width="'100%'"
                            :height="'315px'"
                            :full="true"
                            :maxHeight="315"
                            :search="true"
                            :value="selectingItem"
                            :items="items"
                            :showCount="true"
                            :unselectLastOne="true"
                            @input="onSelectedCidChange"/>
        <div v-else
            class="empty">
          <i class="icon icon--empty"></i><br>
          {{ $t('common.noCollectedData') }}
        </div>
      </div>
      <div class="cid-footer">
        <ui-button class="back"
                   :iconName="'icon--arrow-left-black'"
                   @click="showIndex">{{ $t('common.back') }}</ui-button>
        <ui-button @click="resetSelected"
                   :iconName="'icon--reset'"
                   :disabled="!selectingItem || selectingItem.length < 1">{{ $t('common.reset') }}</ui-button>
        <ui-button @click="applySelected"
                   :disabled="JSON.stringify(selectingItem) === JSON.stringify(selectedCid[selectedTag])"
                   :primary2="true">{{ $t('common.apply') }}</ui-button>
      </div>
    </div>
  </div>
</template>

<script>
import { compare09azAZ } from '@/libs/utils'
import { getCid } from '@/api/report'
import { CID_TAGS } from '@/libs/constants'
import UiButton from '@/components/ui/UiButton'
import UiCheckSelectbox from '@/components/ui/UiCheckSelectbox'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'
import UiSwitch from '@/components/ui/UiSwitch'

export default {
  name: 'CidSetting',
  components: {
    UiButton,
    UiCheckSelectbox,
    UiIconTooltip,
    UiLoadingCircle,
    UiSwitch
  },
  props: ['aid', 'startDate', 'endDate', 'cidCache', 'otherKeySet'],
  methods: {
    showList (tag) {
      this.selectedTag = tag
      this.setCidData(this.cidData)
    },
    setCidData (result) {
      let listItems = {}
      let hasItems = false
      for (const item of result[this.selectedTagKey]) {
        Object.assign(listItems, item)
        hasItems = true
      }
      if (hasItems) {
        this.listItems = { '': {} }

        if (listItems[''] !== undefined) {
          this.listItems[''][this.$t('campaignTargeting.cid.blankInflow', { tag: this.$t('campaignTargeting.cid.' + this.selectedTag) })] = ''
        }

        const keys = Object.keys(listItems)
        keys.sort(compare09azAZ)
        keys.forEach(key => {
          if (key && listItems[key]) {
            const char = key.charAt(0).toLowerCase()
            if (!this.listItems[char]) {
              this.listItems[char] = {}
            }
            this.listItems[char][key] = listItems[key]
          }
        })
        if (this.selectedCid[this.selectedTag]) {
          this.selectedCid[this.selectedTag].sort()
        }
        this.selectingItem = this.selectedCid[this.selectedTag]
      } else {
        this.listItems = false
      }
      this.step = 2
      this.loading = false
    },
    onSelectedCidChange (value) {
      this.selectingItem = value
      this.selectingItem.sort()
    },
    showIndex () {
      if (this.selectedTag && JSON.stringify(this.selectedCid[this.selectedTag]) !== JSON.stringify(this.selectingItem)) {
        this.showConfirm(this.$t('dialog.campaignTargeting.noAppliedAndGoBack.desc'), () => {
          this.resetSelected()
          this.step = 1
          this.selectedTag = null
          this.confirming = false
        })
      } else {
        this.step = 1
        this.selectedTag = null
      }
    },
    resetSelected () {
      this.selectingItem = []
      this.resetKey = new Date().toString()
    },
    applySelected () {
      if (this.otherKeySett) {
        this.showConfirm(this.$t('dialog.campaignTargeting.applyNew.desc'),
          () => {
            this.otherKeySett = false
            this.selectedCid[this.selectedTag] = [...this.selectingItem]
            this.confirming = false
            this.step = 1
            this.selectedTag = null
          })
      } else {
        this.selectedCid[this.selectedTag] = [...this.selectingItem]
        this.step = 1
        this.selectedTag = null
      }
    },
    toggleWithoutValue () {
      this.$set(this.selectedCid, 0, !this.selectedCid[0])
    },
    onCloseLayer (reset, close) {
      let selectedCid = this.selectedCid
      let selectedCidExist = false
      if (selectedCid) {
        if (selectedCid[0]) {
          selectedCidExist = true
        } else {
          for (const tag of this.tagList) {
            if (selectedCid[tag] && selectedCid[tag].length > 0) {
              selectedCidExist = true
            }
          }
        }
      }
      if (!selectedCidExist) {
        selectedCid = null
      }
      if (reset) {
        if (JSON.stringify(selectedCid) !== JSON.stringify(this.initialCid) ||
          (this.selectedTag && ((!this.initialCid && this.selectingItem.length > 0) || (this.initialCid && JSON.stringify(this.initialCid[this.selectedTag]) !== JSON.stringify(this.selectingItem))))) {
          this.showConfirm(this.$t('dialog.campaignTargeting.noAppliedAndClose.desc'), () => { this.$emit('onCloseLayer') })
        } else {
          this.$emit('onSetCid', selectedCid)
          if (close) {
            this.$emit('onCloseLayer')
          }
        }
      } else {
        this.$emit('onSetCid', selectedCid)
        if (close) {
          this.$emit('onCloseLayer')
        }
      }
    },
    showConfirm (description, confirm) {
      this.confirmOption.description = description
      this.confirmOption.confirm = confirm
      this.confirming = true
    }
  },
  created () {
    this.tagList = CID_TAGS
    this.otherKeySett = this.otherKeySet

    this.initialCid = this.cidCache
    this.selectedCid = { }
    for (const tag of this.tagList) {
      this.selectedCid[tag] = this.initialCid ? this.initialCid[tag] : []
    }
    this.$set(this.selectedCid, 0, this.initialCid ? this.initialCid[0] || false : false)

    this.loading = true
    this.step = 1
    getCid({
      startDate: this.startDate,
      endDate: this.endDate,
      aid: this.aid
    })
      .then(result => {
        this.cidData = result
        this.loading = false
      })
  },
  computed: {
    hasData () {
      if (!this.cidData) {
        return false
      }
      let has = false
      if (typeof this.cidData === 'object') {
        Object.values(this.cidData).forEach(params => {
          if (params && typeof params === 'object' && params.length > 0) {
            has = true
          }
        })
      }
      return has
    },
    selectedTagKey () {
      return 'cid.' + this.selectedTag
    },
    items () {
      if (!this.listItems) {
        return false
      }

      let list = Object.entries(this.listItems)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, value]) => {
          return {
            value: key,
            text: key,
            items: Object.entries(value).map(([k, v]) => {
              return {
                value: v === null || v === '' ? v : k,
                text: k
              }
            })
          }
        })

      return list
    }
  },
  data () {
    return {
      step: 1,
      loading: false,
      initialCid: null,
      selectedCid: {},
      selectedTag: null,
      listItems: false,
      selectingItem: [],
      confirming: false,
      otherKeySett: false,
      confirmOption: {
        description: '',
        confirm: () => {}
      },
      cidData: null,
      resetKey: new Date().toString()
    }
  }
}
</script>

<style lang="scss" scoped>
.cid-setting {
  width: 731px;
  height: 526px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;

  &.confirming {
    position: relative;
    width: 360px;
    height: 270px;

    .confirm {
      opacity: 1;
      z-index: 10;
    }

    .step {
      opacity: 0;
      z-index: 1;
    }
  }

  .confirm {
    position: relative;
    text-align: center;
    opacity: 0;

    .confirm-icon {
      padding-top: 40px;
    }

    .confirm-description {
      font-size: 14px;
      color: #444444;
      line-height: 1.43;
      margin-top: 13px;
      padding: 0 38px;
      height: 100px;
    }

    .confirm-buttons {
      .ui-btn {
        margin: 0 2px 0 3px;
        width: 86px;
      }
    }
  }

  .step {
    position: relative;
    background-color: #ffffff;
    border-radius: 15px;
    width: 100%;
    height: 100%;

    &.loading {
      z-index: 100;
    }

    &.step-1 {
      .cid-header {
        position: relative;
        margin: 0 30px;
        border-bottom: 1px solid #bbbbbb;

        .icon {
          @include positionYcenter;
          top: calc(50% + 4px);
          border-right: 1px solid #d5d5d5;
          background-position: center left;
          height: 54px;
          padding-right: 20px;
        }

        h3 {
          font-size: 18px;
          color: #444444;
          line-height: 1.17;
          padding: 34px 0 0 87px;

          .tooltip {
            top: 2px;
            left: 6px;

            & /deep/ {
              .icon {
                width: 15px;
                height: 15px;
                background-size: 100% 100%;
              }

              .bubble {
                margin-bottom: 3px !important;
                margin-left: -10.5px !important;
              }
            }
          }
        }

        p {
          font-size: 13px;
          color: #444444;
          line-height: 1.38;
          padding: 6px 0 23px 88px;

          & /deep/ span {
            color: #ec0047;
          }
        }
      }

      .cid-body {
        margin: 19px 0 11px 0;
        white-space: nowrap;

        .cid-selection {
          text-align: center;
          margin: 5px 0;
          white-space: normal;

          .null-inflow {
            line-height: 1.2;
            color: #444444;
            font-size: 15px;
            margin-bottom: 17px;

            .ui-switch {
              margin-left: 5px;
            }
          }

          .tag-button {
            position: relative;
            vertical-align: top;
            width: 125px;
            height: 125px;
            margin: 1px 6px 10px 5px;
            border: 2px dotted #c3c3c3;
            border-radius: 10px;
            transition: border-color 0.2s ease,
                        background-color 0.2s ease,
                        box-shadow 0.2s ease;
            padding-top: 50px;

            .name {
              position: absolute;
              top: 50%;
              transform: translateY(calc(-50% - 20px));
              line-height: 1.07;
              width: calc(100% - 40px);
              font-size: 15px;
              color: #444444;
              display: block;
              font-weight: bold;
              margin: 5px 20px 12px;
            }

            &.seg .name {
              width: calc(100% - 20px);
              margin-right: 10px;
              margin-left: 10px;
            }

            .count {
              font-size: 12px;
              color: #444444;
              margin-bottom: 11px;
              display: block;
              text-align: center;

              .number {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 7px;
                display: block;
                color: #3eaa08;
              }

              .check {
                position: absolute;
                top: -1px;
                right: -1px;
                width: 19px;
                height: 19px;
                padding: 4px 4px 16px 16px;
                border-top-right-radius: 10px;
                background: url('../../../assets/img/campaign-button-bg.svg') transparent no-repeat right -30px top -30px;
                transition: background-position 0.2s ease;

                i {
                  width: 16px;
                  height: 16px;
                }
              }
            }

            i {
              width: 20px;
              height: 20px;
            }

            &.selected {
              border: 2px solid #3eaa08;
              background-color: rgba(62, 170, 8, 0.03);

              .name {
                transform: translateY(calc(-50% - 27px));
              }

              .count {
                .check {
                  background-position: right 0 top 0;
                }
              }
            }

            &:hover {
              border: 2px solid #0274ff;
              background-color: #ffffff;
              box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.25);

              .count {
                .check {
                  background-position: right -30px top -30px;
                }
              }
            }
          }
        }

        .cid-empty {
          margin: 105px 0 112px 0;
          height: 127px;
          text-align: center;
          line-height: 1.46;
          font-size: 14px;
          color: #555555;

          i {
            margin-top: 3px;
            margin-bottom: 3px;
          }
        }
      }

      .cid-footer {
        margin: 0 15px 20px;
        text-align: center;
        position: relative;

        .ui-btn {
          margin: 0 2px 0 3px;
          width: 95px;

          &.back {
            position: absolute;
            top: 0;
            left: 0;
            border: none;
            font-size: 13px;
            width: 75px;
            text-align: center;
          }
        }
      }
    }

    &.step-2 {
      .cid-header {
        position: relative;
        margin: 0 32px;

        h2 {
          font-size: 18px;
          color: #444444;
          padding-top: 40px;

          & /deep/ span {
            color: #ec0047;
          }
        }

        p {
          font-size: 13px;
          line-height: 1.08;
          color: #888888;
          margin: 10px 0 22px;
        }

        .btn-close {
          position: absolute;
          top: 12px;
          right: -19px;
        }
      }

      .cid-body {
        padding: 14px 30px;

        .empty {
          width: 100%;
          height: 312px;
          border-radius: 5px;
          border: 1px solid #d7d7d7;
          background-color: #f4f4f4;
          line-height: 1.77;
          color: #666666;
          font-size: 14px;
          text-align: center;
          margin-bottom: 3px;

          i {
            margin-top: 104px;
          }
        }
      }

      .cid-footer {
        margin: 14px 15px;
        text-align: center;
        position: relative;

        .ui-btn {
          margin: 0 2px 0 3px;
          width: 95px;

          &.back {
            position: absolute;
            top: 0;
            left: 0;
            border: none;
            font-size: 13px;
            width: 75px;
            text-align: center;
          }
        }
      }
    }
  }
}
</style>
